import { useCallback } from 'react';
import posthog, { isPostHogActive } from '@/lib/posthog';
import api from '@/lib/api';

// ── Meta Pixel mapping ──────────────────────────────────────────────────────
const META_PIXEL_MAP = {
  product_viewed: 'ViewContent',
  add_to_cart: 'AddToCart',
  checkout_started: 'InitiateCheckout',
  payment_initiated: 'AddPaymentInfo',
  purchase: 'Purchase',
  signup_completed: 'CompleteRegistration',
};

// Events we ALSO mirror to the server-side Conversions API (high-value funnel
// events where attribution loss to ad-blockers / iOS 14.5+ hurts the most).
const CAPI_MIRROR = new Set(['AddToCart', 'InitiateCheckout', 'Purchase', 'CompleteRegistration']);

// RFC4122-ish v4 UUID using crypto when available; falls back to Math.random.
function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // eslint-disable-next-line no-bitwise
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function buildFbProps(properties) {
  const fbProps = {};
  if (properties?.price || properties?.revenue) {
    fbProps.value = properties.revenue || properties.price;
    fbProps.currency = properties.currency || 'INR';
  }
  if (properties?.product_name) fbProps.content_name = properties.product_name;
  if (properties?.product_id) fbProps.content_ids = [properties.product_id];
  if (properties?.content_ids) fbProps.content_ids = properties.content_ids;
  if (properties?.category) fbProps.content_category = properties.category;
  if (properties?.num_items) fbProps.num_items = properties.num_items;
  if (properties?.contents) fbProps.contents = properties.contents;
  if (properties?.product_id || properties?.content_ids) fbProps.content_type = 'product';
  return fbProps;
}

function buildCapiPayload(eventName, eventId, properties) {
  const custom_data = {};
  if (properties?.revenue || properties?.price) {
    custom_data.value = properties.revenue || properties.price;
    custom_data.currency = properties.currency || 'INR';
  }
  if (properties?.product_id) custom_data.content_ids = [properties.product_id];
  if (properties?.content_ids) custom_data.content_ids = properties.content_ids;
  if (properties?.product_name) custom_data.content_name = properties.product_name;
  if (properties?.num_items) custom_data.num_items = properties.num_items;
  if (properties?.contents) custom_data.contents = properties.contents;
  if (properties?.order_id) custom_data.order_id = properties.order_id;
  if (properties?.product_id || properties?.content_ids) custom_data.content_type = 'product';

  const user_data = {};
  if (properties?.email) user_data.email = properties.email;
  if (properties?.phone) user_data.phone = properties.phone;
  if (properties?.first_name) user_data.first_name = properties.first_name;
  if (properties?.last_name) user_data.last_name = properties.last_name;
  if (properties?.user_id || properties?.customer_id) {
    user_data.external_id = properties.user_id || properties.customer_id;
  }

  return {
    event_name: eventName,
    event_id: eventId,
    event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
    user_data: Object.keys(user_data).length ? user_data : undefined,
    custom_data: Object.keys(custom_data).length ? custom_data : undefined,
  };
}

function fireFbq(eventName, properties, eventId) {
  const fbEvent = META_PIXEL_MAP[eventName];
  if (!fbEvent || typeof window.fbq !== 'function') return null;
  const fbProps = buildFbProps(properties);
  // Pass eventID via the 4th arg so Meta deduplicates browser+server events.
  if (eventId) {
    window.fbq('track', fbEvent, fbProps, { eventID: eventId });
  } else {
    window.fbq('track', fbEvent, fbProps);
  }
  return fbEvent;
}

function fireCapi(metaEventName, properties, eventId) {
  if (!metaEventName || !CAPI_MIRROR.has(metaEventName)) return;
  const body = buildCapiPayload(metaEventName, eventId, properties);
  // Fire-and-forget; never block UX on tracking.
  api.post('/capi/event', body).catch(() => { /* swallow */ });
}

// ── GA4 + dataLayer (unchanged) ─────────────────────────────────────────────
const GA4_EVENT_MAP = {
  product_viewed: 'view_item',
  product_searched: 'search',
  add_to_cart: 'add_to_cart',
  cart_viewed: 'view_cart',
  checkout_started: 'begin_checkout',
  payment_initiated: 'add_payment_info',
  purchase: 'purchase',
  signup_completed: 'sign_up',
  coupon_applied: 'select_promotion',
};

function fireGtag(eventName, properties) {
  if (typeof window.gtag !== 'function') return;
  const ga4Event = GA4_EVENT_MAP[eventName];
  if (!ga4Event) return;
  const params = {};
  if (properties?.revenue || properties?.price) params.value = properties.revenue || properties.price;
  if (properties?.currency) params.currency = properties.currency;
  else params.currency = 'INR';
  if (properties?.product_id) params.items = [{ item_id: properties.product_id, item_name: properties.product_name || '', price: properties.price || 0, quantity: properties.quantity || 1 }];
  if (properties?.order_id) params.transaction_id = properties.order_id;
  if (properties?.query) params.search_term = properties.query;
  if (properties?.coupon_code) params.coupon = properties.coupon_code;
  window.gtag('event', ga4Event, params);
}

function fireDataLayer(eventName, properties) {
  if (typeof window.dataLayer === 'undefined') return;
  window.dataLayer.push({ event: eventName, ...properties });
}

// ── Public API ──────────────────────────────────────────────────────────────
export function trackEvent(eventName, properties = {}) {
  if (isPostHogActive) {
    posthog.capture(eventName, properties);
  }
  // Generate a single event_id used for BOTH the browser Pixel and the
  // server CAPI call so Meta deduplicates them.
  const eventId = uuid();
  const metaEventName = fireFbq(eventName, properties, eventId);
  fireCapi(metaEventName, properties, eventId);
  fireGtag(eventName, properties);
  fireDataLayer(eventName, properties);
}

export function identifyUser(userId, traits = {}) {
  if (isPostHogActive) {
    posthog.identify(userId, traits);
  }
}

export function resetUser() {
  if (isPostHogActive) {
    posthog.reset();
  }
}

export function useTracking() {
  const track = useCallback((eventName, properties = {}) => {
    trackEvent(eventName, properties);
  }, []);

  const identify = useCallback((userId, traits = {}) => {
    identifyUser(userId, traits);
  }, []);

  const reset = useCallback(() => {
    resetUser();
  }, []);

  return { trackEvent: track, identifyUser: identify, resetUser: reset };
}
